import PaginatedSelect, {
  SelectedValue,
} from "@/components/ui/PaginatedSelect";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { cn } from "@/lib/utils";
import { usePromptRegistryObjects } from "@/queries";
import { PromptRegistry } from "@/types/prompt-registry";
import { UseQueryResult } from "@tanstack/react-query";
import { Search, X } from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { NameWithId } from "../ui/PaginatedSelect/types";

interface TemplateSelectDropdownProps {
  selectedTemplateName?: string;
  selectedTemplateId?: number;
  handleTemplateSelection: ({ id, name }: { id: number; name: string }) => void;
  className?: string;
  dropdownTriggerClassName?: string;
  sourceTemplateId?: number | null;
  isSnippet?: boolean;
  disabled?: boolean;
  size?: "sm" | "md";
  onClear?: () => void;
  placeholder?: string;
}

export default function TemplateSelectDropdown({
  selectedTemplateName,
  selectedTemplateId,
  handleTemplateSelection,
  className,
  dropdownTriggerClassName,
  sourceTemplateId = null,
  size = "md",
  isSnippet = false,
  disabled = false,
  onClear,
  placeholder = "Select a prompt...",
}: TemplateSelectDropdownProps) {
  const userContext = useUser();
  const auth = useAuth();
  const userToken = auth?.userToken!;
  const workspaceId = userContext.activeWorkspaceId!;
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const baseQuery = usePromptRegistryObjects(userToken, {
    workspaceId,
    perPage: 6,
    q: searchQuery,
    templateType: isSnippet ? "completion" : undefined,
  });

  // Reset page when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Fetch pages as needed
  useEffect(() => {
    if (
      baseQuery.hasNextPage &&
      (baseQuery.data?.pages.length ?? 0) < currentPage
    ) {
      baseQuery.fetchNextPage();
    }
  }, [currentPage, baseQuery]);

  const TemplateQuery = (page: number): UseQueryResult<any, unknown> => {
    // Update current page when PaginatedSelect requests a different page
    if (page !== currentPage) {
      setCurrentPage(page);
    }
    return baseQuery as any;
  };

  const extractTemplateData = useCallback(
    (data: any) => {
      if (!data) return { data: [], totalPages: 0 };

      // For a specific page, we need to extract just that page's data
      const pageIndex = currentPage - 1;
      const pageData = data.pages[pageIndex];

      if (!pageData) return { data: [], totalPages: 0 };

      const templates = pageData.items.filter(
        (template: PromptRegistry) => template.id !== sourceTemplateId,
      );

      const totalPages = pageData.pages || 0;

      return {
        data: templates,
        totalPages,
      };
    },
    [sourceTemplateId, currentPage],
  );

  const renderTemplateItem = useCallback((template: PromptRegistry) => {
    return (
      <div className="flex flex-col gap-0.5">
        <span className="font-medium">{template.prompt_name}</span>
        {template.folder_id && (
          <span className="text-xs text-gray-500">
            Folder: {template.folder_name || "Unknown"}
          </span>
        )}
      </div>
    );
  }, []);

  const handleValueChange = useCallback(
    (value: SelectedValue<PromptRegistry>) => {
      handleTemplateSelection({
        id: value.item.id,
        name: value.item.prompt_name,
      });
    },
    [handleTemplateSelection],
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
    },
    [],
  );

  const filterComponent = useMemo(
    () => (
      <div className="px-2 py-2">
        <div className="relative">
          <Input
            ref={searchInputRef}
            placeholder="Search templates..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="h-8 pl-8"
            autoFocus
          />
          <Search className="pointer-events-none absolute left-2 top-2 h-4 w-4 text-gray-500" />
          {searchQuery && (
            <Button
              variant="ghost"
              size="tinyIcon"
              onClick={() => {
                setSearchQuery("");
                setTimeout(() => {
                  searchInputRef.current?.focus();
                }, 0);
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    ),
    [searchQuery, handleSearchChange],
  );

  const selectedValue =
    selectedTemplateId && selectedTemplateName
      ? {
          id: selectedTemplateId.toString(),
          item: {
            id: selectedTemplateId.toString(),
            name: selectedTemplateName,
          } as NameWithId,
        }
      : undefined;

  return (
    <div className="w-full">
      <PaginatedSelect<PromptRegistry, any>
        query={TemplateQuery}
        renderItem={renderTemplateItem}
        getItemValue={(item) => item.id.toString()}
        placeholder={placeholder}
        onValueChange={handleValueChange}
        value={selectedValue}
        className={cn(
          "w-full",
          size === "sm" ? "h-[32px] rounded-md pl-3 text-xs" : "",
          dropdownTriggerClassName,
          className,
        )}
        disabled={disabled}
        onRemove={onClear}
        extractData={extractTemplateData}
        filterComponent={filterComponent}
      />
    </div>
  );
}
